import React, {useEffect, useState} from 'react';
import SegmentMultiple from '@/pages/datalab/components/SegmentMultiple';
import {history, useIntl} from 'umi';
import {LANGUAGE_EN} from "@home/_config/config";
import {useMemoizedFn, useUpdateEffect} from "ahooks";
import {updateUrlParams} from '@/pages/datalab/utils/utils';
import {isEmpty} from 'lodash-es';

/**
 * 数据表管理页面  四个类别的筛选交互
 * 1. 可以多选; 必然要有一个选项被选中, 全部和其他选项互斥
 * 2. 默认进入页面都是选中全部
 * 3. 选中任意一个类别的选项, 其他类别的选项都要重新计算并渲染, 被选中的类别的选项不重新计算.
 * 4. 如果重新计算后, 原本被选中的所有选项都不在新的选项中, 选中态要重置为 全部
 * 5. 当用户点击任意类别的全部选项后, 所有类别的 选项都被恢复为 初始的所有选项
 *
 * 数据情况补充:
 *     之前是后端返回的选项中如果有空字符串, 则前端逻辑归类为Others选项;
 *     现在数据上不允许有空字符串的情况, 如果出现属于数据错误, 需要在数据端修改
 */

const CYCLE = 'cycle'; // 周期
// const DRIVER = 'driver'; // 数据库
const LABEL = 'label'; // 一级类目
const SUB_LABEL = 'sub_label'; // 二级类目
// const CATEGORIES = [LABEL, SUB_LABEL, CYCLE];

type Tab = {
  count: number; // 类别的数量
  cycle: string; // 周期
  driver: string; // 数据库类型
  label: string; // 一级类目
  sub_label: string; // 二级类目
  [key: string]: string | number;
}

// 后端返回的中英文对照结构
type TranslateObject = {
  [key: string]: {
    en: string;
    zh: string;
  };
}

type TCategryOption = {
  text: string;
  textCn: string;
  textEn: string;
  value: string;
}

const prepareOptions = (source: Record<string, number>, trans: TranslateObject, isEn: boolean) => {
  
  const options = Object.keys(source).map(key => {
    
    const textCn = `${trans?.[key]?.zh || key}(${source[key]})`;
    const textEn = `${trans?.[key]?.en || key}(${source[key]})`;
    return {
      text: isEn ? textEn : textCn,
      textCn,
      textEn,
      value: key,
    };
  });
  
  options.sort((a, b) => {
    // Others选项排在最后
    if (a.value === 'Others') return 1;
    if (b.value === 'Others') return -1;
    
    return a.value.localeCompare(b.value);
  });
  
  return [{ text: isEn ? 'All' : '全部', textCn: '全部', textEn: 'All', value: 'all' }, ...options];
};

export default function FilterTabs(props: {categories: {tabs: Tab[]; trans: Record<string, TranslateObject>;}; filterValue: Record<string, string[]>; setFilterValue: (value: Record<string, string[]>) => void;}) {
  const {formatMessage, locale} = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const {categories, filterValue, setFilterValue} = props;
  const {tabs=[], trans={}} = categories;

  const [optionsMap, setOptionsMap] = useState<Record<string, TCategryOption[]>>({[LABEL]: [], [SUB_LABEL]: [], [CYCLE]: []});

  const getOptions = useMemoizedFn((value: Record<string, string[]>, oldOptionMap?: Record<string, TCategryOption[]> | undefined) => {

    const {cycle, label, sub_label} = value;
    const filteredData = tabs.filter(item => {
      const cycleMatch = (cycle.includes('all') || cycle.includes(item.cycle));
      const labelMatch = (label.includes('all') || label.includes(item.label));
      const subLabelMatch = (sub_label.includes('all') || sub_label.includes(item.sub_label));

      return cycleMatch && labelMatch && subLabelMatch;
    });

    const cycleCounts: Record<string, number> = {};
    const labelCounts: Record<string, number> = {};
    const subLabelCounts: Record<string, number> = {};

    const lastType = history?.location?.query?.lastClickCategoryType;

    filteredData.forEach(item => {
      cycleCounts[item.cycle] = (cycleCounts[item.cycle] || 0) + item.count;
      labelCounts[item.label] = (labelCounts[item.label] || 0) + item.count;
      subLabelCounts[item.sub_label] = (subLabelCounts[item.sub_label] || 0) + item.count;
    });
    const cycleOptions = (lastType === CYCLE && !value[CYCLE].includes('all')) ? oldOptionMap?.[CYCLE] : prepareOptions(cycleCounts, trans[CYCLE], isEn);
    const labelOptions = (lastType === LABEL && !value[LABEL].includes('all')) ? oldOptionMap?.[LABEL] : prepareOptions(labelCounts, trans[LABEL], isEn);
    const subLabelOptions = (lastType === SUB_LABEL && !value[SUB_LABEL].includes('all')) ? oldOptionMap?.[SUB_LABEL] : prepareOptions(subLabelCounts, trans[SUB_LABEL], isEn);

    return {[LABEL]: labelOptions ?? [], [SUB_LABEL]: subLabelOptions ?? [], [CYCLE]: cycleOptions ?? []};
  });

  useEffect(() => {

    let oldOptionMap: Record<string, TCategryOption[]> = optionsMap;

    if (isEmpty(optionsMap[LABEL]) && isEmpty(optionsMap[SUB_LABEL]) && isEmpty(optionsMap[CYCLE])) {
      const lastType = history?.location?.query?.lastClickCategoryType;
      oldOptionMap = getOptions({[LABEL]: lastType === LABEL ? 'all' : filterValue[LABEL], [SUB_LABEL]: lastType === SUB_LABEL ? 'all' : filterValue[SUB_LABEL], [CYCLE]: lastType === CYCLE ? 'all' : filterValue[CYCLE]});
    }

    setOptionsMap(getOptions(filterValue, oldOptionMap));
  }, [filterValue]);

  useEffect(() => {

    // 把 分类选项数据 存入 sessionStorage, 当进入数据表编辑页面时, 从 sessionStorage 中获取分类选项数据, 用于下拉选项的展示
    if (categories) {
      const allOptionsMap = getOptions({[CYCLE]: 'all', [LABEL]: 'all', [SUB_LABEL]: 'all'});
      sessionStorage.setItem('dbl-table-options', JSON.stringify(allOptionsMap));
    }

  }, [categories]);
  
  useUpdateEffect(() => {
    
    const labelOptions = optionsMap[LABEL].map((opt: TCategryOption) => ({
      ...opt,
      text: isEn ? opt.textEn : opt.textCn,
    }));
    
    const subLabelOptions = optionsMap[SUB_LABEL].map((opt: TCategryOption) => ({
      ...opt,
      text: isEn ? opt.textEn : opt.textCn,
    }));
    
    const cycleOptions = optionsMap[CYCLE].map((opt: TCategryOption) => ({
      ...opt,
      text: isEn ? opt.textEn : opt.textCn,
    }));
    
    const newOptionsMap = {[LABEL]: labelOptions, [SUB_LABEL]: subLabelOptions, [CYCLE]: cycleOptions};
    setOptionsMap(newOptionsMap);
    
  }, [isEn]);
  
  const handleSelect = (type: string, selectedValues: string[]) => {
    if (selectedValues.length === 0) return;

    if (selectedValues.includes('all')) {
      if (filterValue[type]?.includes('all')) {
        selectedValues.shift();
        setFilterValue({...filterValue, [type]: selectedValues});
      } else {
        setFilterValue({...filterValue, [type]: ['all']});
      }
      
    } else {
      setFilterValue({...filterValue, [type]: selectedValues});
    }
    updateUrlParams({
      lastClickCategoryType: type,
      sort: { by: 'use_count', order: 'desc' },
      quickValue: { drivers: [], db_name_filters: [] },
    });
  };
  
  
  return (
    <>
      <SegmentMultiple
        labelWidth={isEn ? 140 : 70}
        label={formatMessage({id: 'dbl_data_table_level_one'})}
        key={2}
        options={optionsMap[LABEL]}
        onChange={(selectedValue: string[]) => handleSelect(LABEL, selectedValue)}
        value={filterValue[LABEL]}
      />
      <SegmentMultiple
        labelWidth={isEn ? 140 : 70}
        label={formatMessage({id: 'dbl_data_table_level_two'})}
        key={3}
        options={optionsMap[SUB_LABEL]}
        onChange={(selectedValue: string[]) => handleSelect(SUB_LABEL, selectedValue)}
        value={filterValue[SUB_LABEL]}
      />
      <SegmentMultiple
        labelWidth={isEn ? 140 : 70}
        label={formatMessage({id: 'dbl_data_table_cycle'})}
        key={4}
        options={optionsMap[CYCLE]}
        onChange={(selectedValue: string[]) => handleSelect(CYCLE, selectedValue)}
        value={filterValue[CYCLE]}
      />
    </>
  );
}
